import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Switch } from 'shared/ui/switch'
import { Checkbox } from 'shared/ui/checkbox'
import { Separator } from 'shared/ui/separator'
import { Cloud, Loader2, Shield } from 'lucide-react'
import { toast } from 'sonner'
import {
  createAzureAuthConfig,
  searchAzureAuthConfigs,
  updateAzureAuthConfig,
} from 'modules/admin/api/azureAuthConfigService'

interface AzureConfig {
  id?: string
  name: string
  directoryTenantId: string
  clientId: string
  authority: string
  redirectUri: string
  issuer: string
  metadataAddress: string
  contractorDirectoryTenantId: string
  contractorClientId: string
  contractorAuthority: string
  contractorRedirectUri: string
  contractorIssuer: string
  contractorMetadataAddress: string
  enableEmployee: boolean
  enableContractor: boolean
  isActive: boolean
}

export function AzureAdConfiguration() {
  const [config, setConfig] = useState<AzureConfig>({
    id: undefined,
    name: 'IRPC Azure AD',
    directoryTenantId: '',
    clientId: '',
    authority: '',
    redirectUri: '',
    issuer: '',
    metadataAddress: '',
    contractorDirectoryTenantId: '',
    contractorClientId: '',
    contractorAuthority: '',
    contractorRedirectUri: '',
    contractorIssuer: '',
    contractorMetadataAddress: '',
    enableEmployee: true,
    enableContractor: false,
    isActive: true,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const update = (patch: Partial<AzureConfig>) => setConfig((prev) => ({ ...prev, ...patch }))

  const computedAuthority = useMemo(() => {
    if (!config.directoryTenantId.trim()) return ''
    return `https://login.microsoftonline.com/${config.directoryTenantId.trim()}`
  }, [config.directoryTenantId])

  const computedMetadataAddress = useMemo(() => {
    if (!config.directoryTenantId.trim()) return ''
    return `${computedAuthority}/v2.0/.well-known/openid-configuration`
  }, [computedAuthority, config.directoryTenantId])

  const computedContractorAuthority = useMemo(() => {
    if (!config.contractorDirectoryTenantId.trim()) return ''
    return `https://login.microsoftonline.com/${config.contractorDirectoryTenantId.trim()}`
  }, [config.contractorDirectoryTenantId])

  const computedContractorMetadataAddress = useMemo(() => {
    if (!config.contractorDirectoryTenantId.trim()) return ''
    return `${computedContractorAuthority}/v2.0/.well-known/openid-configuration`
  }, [computedContractorAuthority, config.contractorDirectoryTenantId])

  const loadExisting = async () => {
    try {
      setIsLoading(true)
      const res = await searchAzureAuthConfigs({ page: 1, pageSize: 1 })
      const first = res.items?.[0]
      if (first) {
        setConfig({
          id: first.azureAuthConfigId,
          name: first.name,
          directoryTenantId: first.directoryTenantId,
          clientId: first.clientId,
          authority: first.authority ?? '',
          redirectUri: first.redirectUri ?? '',
          issuer: first.issuer ?? '',
          metadataAddress: first.metadataAddress ?? '',
          contractorDirectoryTenantId: first.contractorDirectoryTenantId ?? '',
          contractorClientId: first.contractorClientId ?? '',
          contractorAuthority: first.contractorAuthority ?? '',
          contractorRedirectUri: first.contractorRedirectUri ?? '',
          contractorIssuer: first.contractorIssuer ?? '',
          contractorMetadataAddress: first.contractorMetadataAddress ?? '',
          enableEmployee: first.enableEmployee,
          enableContractor: first.enableContractor,
          isActive: first.isActive,
        })
      }
    } catch (err) {
      console.error(err)
      toast.error('โหลดการตั้งค่า Azure AD ไม่สำเร็จ')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadExisting()
  }, [])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const payload = {
        tenantId: undefined,
        name: config.name.trim(),
        directoryTenantId: config.directoryTenantId.trim(),
        clientId: config.clientId.trim(),
        authority: config.authority.trim() || null,
        redirectUri: config.redirectUri.trim() || null,
        issuer: config.issuer.trim() || null,
        metadataAddress: config.metadataAddress.trim() || null,
        contractorDirectoryTenantId: config.contractorDirectoryTenantId.trim() || null,
        contractorClientId: config.contractorClientId.trim() || null,
        contractorAuthority: config.contractorAuthority.trim() || null,
        contractorRedirectUri: config.contractorRedirectUri.trim() || null,
        contractorIssuer: config.contractorIssuer.trim() || null,
        contractorMetadataAddress: config.contractorMetadataAddress.trim() || null,
        enableEmployee: config.enableEmployee,
        enableContractor: config.enableContractor,
        isActive: config.isActive,
      }

      if (config.id) {
        const updated = await updateAzureAuthConfig(config.id, payload)
        setConfig((prev) => ({ ...prev, id: updated.azureAuthConfigId }))
        toast.success('บันทึกการตั้งค่า Azure AD สำเร็จ')
      } else {
        const created = await createAzureAuthConfig(payload)
        setConfig((prev) => ({ ...prev, id: created.azureAuthConfigId }))
        toast.success('สร้างการตั้งค่า Azure AD สำเร็จ')
      }
    } catch (err) {
      console.error(err)
      toast.error('บันทึกการตั้งค่า Azure AD ไม่สำเร็จ')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="border-sky-200 bg-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sky-800">
          <Shield className="w-5 h-5" />
          Azure AD Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <Cloud className="w-4 h-4" />
          <AlertDescription>
            ตั้งค่าการเชื่อมต่อ Azure AD สำหรับการยืนยันตัวตน ผู้ใช้ต้องมีบัญชีใน Azure และข้อมูลในระบบต้องตรงกัน
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <section className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Employee Azure AD</h3>
                <p className="text-xs text-slate-500">ค่าหลักสำหรับการเชื่อมต่อของพนักงาน</p>
              </div>
              <div>
                <Label>ชื่อการเชื่อมต่อ</Label>
                <Input value={config.name} onChange={(e) => update({ name: e.target.value })} />
              </div>
              <div>
                <Label>Directory Tenant ID</Label>
                <Input
                  value={config.directoryTenantId}
                  onChange={(e) => update({ directoryTenantId: e.target.value })}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </div>
              <div>
                <Label>Client ID (Application)</Label>
                <Input
                  value={config.clientId}
                  onChange={(e) => update({ clientId: e.target.value })}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </div>
              <Separator />
              <div>
                <Label>Authority (optional)</Label>
                <Input
                  value={config.authority}
                  onChange={(e) => update({ authority: e.target.value })}
                  placeholder={computedAuthority || 'https://login.microsoftonline.com/<tenantId>'}
                />
              </div>
              <div>
                <Label>Redirect URI (optional)</Label>
                <Input
                  value={config.redirectUri}
                  onChange={(e) => update({ redirectUri: e.target.value })}
                  placeholder="http://localhost:3000/login/employee/azure/callback"
                />
              </div>
              <div>
                <Label>Issuer (optional)</Label>
                <Input
                  value={config.issuer}
                  onChange={(e) => update({ issuer: e.target.value })}
                  placeholder={computedAuthority ? `${computedAuthority}/v2.0` : 'https://login.microsoftonline.com/<tenantId>/v2.0'}
                />
              </div>
              <div>
                <Label>Metadata Address (optional)</Label>
                <Input
                  value={config.metadataAddress}
                  onChange={(e) => update({ metadataAddress: e.target.value })}
                  placeholder={
                    computedMetadataAddress ||
                    'https://login.microsoftonline.com/<tenantId>/v2.0/.well-known/openid-configuration'
                  }
                />
              </div>
              <div className="rounded-md border border-slate-200 bg-white/70 p-3 text-sm text-slate-700">
                <p className="font-medium mb-1 text-slate-800">ค่าแนะนำ (Employee)</p>
                <p>Authority: {computedAuthority || '-'}</p>
                <p>Issuer: {computedAuthority ? `${computedAuthority}/v2.0` : '-'}</p>
                <p>Metadata: {computedMetadataAddress || '-'}</p>
              </div>
            </section>

            {config.enableContractor ? (
              <section className="rounded-lg border border-orange-100 bg-orange-50 p-4 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-orange-800">Contractor Azure AD</h3>
                  <p className="text-xs text-orange-700">กรณีใช้ tenant แยกสำหรับผู้รับเหมา</p>
                </div>
                <div>
                  <Label>Contractor Directory Tenant ID</Label>
                  <Input
                    value={config.contractorDirectoryTenantId}
                    onChange={(e) => update({ contractorDirectoryTenantId: e.target.value })}
                    placeholder="00000000-0000-0000-0000-000000000000"
                  />
                </div>
                <div>
                  <Label>Contractor Client ID (Application)</Label>
                  <Input
                    value={config.contractorClientId}
                    onChange={(e) => update({ contractorClientId: e.target.value })}
                    placeholder="00000000-0000-0000-0000-000000000000"
                  />
                </div>
                <Separator />
                <div>
                  <Label>Contractor Authority (optional)</Label>
                  <Input
                    value={config.contractorAuthority}
                    onChange={(e) => update({ contractorAuthority: e.target.value })}
                    placeholder={computedContractorAuthority || 'https://login.microsoftonline.com/<tenantId>'}
                  />
                </div>
                <div>
                  <Label>Contractor Redirect URI (optional)</Label>
                  <Input
                    value={config.contractorRedirectUri}
                    onChange={(e) => update({ contractorRedirectUri: e.target.value })}
                    placeholder="http://localhost:3000/login/contractor/azure/callback"
                  />
                </div>
                <div>
                  <Label>Contractor Issuer (optional)</Label>
                  <Input
                    value={config.contractorIssuer}
                    onChange={(e) => update({ contractorIssuer: e.target.value })}
                    placeholder={
                      computedContractorAuthority
                        ? `${computedContractorAuthority}/v2.0`
                        : 'https://login.microsoftonline.com/<tenantId>/v2.0'
                    }
                  />
                </div>
                <div>
                  <Label>Contractor Metadata Address (optional)</Label>
                  <Input
                    value={config.contractorMetadataAddress}
                    onChange={(e) => update({ contractorMetadataAddress: e.target.value })}
                    placeholder={
                      computedContractorMetadataAddress ||
                      'https://login.microsoftonline.com/<tenantId>/v2.0/.well-known/openid-configuration'
                    }
                  />
                </div>
                <div className="rounded-md border border-orange-200 bg-white/70 p-3 text-sm text-orange-900">
                  <p className="font-medium mb-1">ค่าแนะนำ (Contractor)</p>
                  <p>Authority: {computedContractorAuthority || '-'}</p>
                  <p>Issuer: {computedContractorAuthority ? `${computedContractorAuthority}/v2.0` : '-'}</p>
                  <p>Metadata: {computedContractorMetadataAddress || '-'}</p>
                </div>
              </section>
            ) : null}
          </div>

          <div className="space-y-6">
            <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Access & Status</h3>
                <p className="text-xs text-slate-500">กำหนดสิทธิ์การใช้งานและสถานะการเชื่อมต่อ</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="block">Enable Employee Login</Label>
                  <p className="text-xs text-gray-500">อนุญาตให้ Employee ใช้ Azure AD</p>
                </div>
                <Switch checked={config.enableEmployee} onCheckedChange={(checked) => update({ enableEmployee: checked })} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="block">Enable Contractor Login</Label>
                  <p className="text-xs text-gray-500">อนุญาตให้ Contractor ใช้ Azure AD</p>
                </div>
                <Switch
                  checked={config.enableContractor}
                  onCheckedChange={(checked) => update({ enableContractor: checked })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="azure-is-active"
                  checked={config.isActive}
                  onCheckedChange={(checked) => update({ isActive: !!checked })}
                />
                <Label htmlFor="azure-is-active">ใช้งานการเชื่อมต่อ (Active)</Label>
              </div>
            </section>

            <div className="flex items-center gap-2">
              <Button onClick={handleSave} disabled={isSaving || isLoading}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              หากไม่ได้กรอก Authority/Issuer/Metadata ระบบจะคำนวณจาก Tenant ID
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default AzureAdConfiguration
